import { useState, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Link2, QrCode, Copy, Check, Download, ExternalLink, Megaphone, BookMarked, CreditCard, Video, Printer, Mail, Image } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import LinkPageEditor from '@/components/LinkPageEditor';
import BusinessCardGenerator from '@/components/BusinessCardGenerator';
import BookmarkGenerator from '@/components/BookmarkGenerator';
import BooktrailerGenerator from '@/components/BooktrailerGenerator';
import BannerGenerator from '@/components/BannerGenerator';

const SITE_URL = window.location.origin;

const ProMarketing = ({ user, token }) => {
  const [activeSection, setActiveSection] = useState('link');

  const menuItems = [
    { id: 'link', icon: Link2, label: 'Mi Enlace Único', available: true },
    { id: 'cards', icon: CreditCard, label: 'Tarjetas', available: true },
    { id: 'bookmarks', icon: BookMarked, label: 'Separadores', available: true },
    { id: 'booktrailer', icon: Video, label: 'Booktrailers', available: true },
    { id: 'banners', icon: Image, label: 'Banners', available: true },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-violet-600 to-purple-600 rounded-2xl p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <Megaphone className="w-8 h-8" />
          <h2 className="text-2xl font-bold">Pro-Marketing</h2>
        </div>
        <p className="text-white/80">
          Herramientas profesionales para promocionar tu contenido y aumentar tus ventas.
        </p>
      </div>

      {/* Navigation Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {menuItems.map((item) => (
          <button
            key={item.id}
            onClick={() => item.available && setActiveSection(item.id)}
            disabled={!item.available}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl whitespace-nowrap transition-all ${
              activeSection === item.id
                ? 'bg-violet-600 text-white'
                : item.available
                ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                : 'bg-gray-50 text-gray-400 cursor-not-allowed'
            }`}
          >
            <item.icon className="w-4 h-4" />
            <span>{item.label}</span>
            {item.comingSoon && (
              <span className="text-xs bg-amber-500/20 text-amber-600 px-2 py-0.5 rounded-full">
                Pronto
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content Sections */}
      {activeSection === 'link' && (
        <LinkPageEditor user={user} token={token} />
      )}

      {activeSection === 'cards' && (
        <BusinessCardGenerator user={user} token={token} />
      )}

      {activeSection === 'bookmarks' && (
        <BookmarkGenerator user={user} token={token} />
      )}

      {activeSection === 'booktrailer' && (
        <BooktrailerGenerator user={user} token={token} />
      )}

      {activeSection === 'banners' && (
        <BannerGenerator user={user} token={token} />
      )}

      {/* Print Services Promo */}
      <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 text-white">
        <div className="flex flex-col md:flex-row items-center gap-6">
          <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center shrink-0">
            <Printer className="w-8 h-8" />
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="text-xl font-bold mb-1">Servicio de Impresión Profesional</h3>
            <p className="text-white/70 text-sm mb-2">
              ¿Necesitas imprimir tus materiales? Ofrecemos impresión de calidad con entrega a domicilio.
            </p>
            <p className="text-amber-400 font-semibold">
              1,000 tarjetas de presentación desde $697 MXN
            </p>
          </div>
          <Button
            onClick={() => {
              const subject = encodeURIComponent(`Solicitud de Impresión - ${user?.full_name}`);
              const body = encodeURIComponent(`Hola,\n\nMe gustaría solicitar información sobre el servicio de impresión.\n\nNombre: ${user?.full_name}\nEmail: ${user?.email}\nUsuario: @${user?.username}\n\nGracias.`);
              window.location.href = `mailto:print@viubux.com?subject=${subject}&body=${body}`;
              toast.success('Abriendo cliente de correo...');
            }}
            className="bg-white text-gray-900 hover:bg-gray-100 shrink-0"
          >
            <Mail className="w-4 h-4 mr-2" />
            Ordenar Servicio de Impresión
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProMarketing;
